import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router';
import { gql, useMutation } from '@apollo/client';
import styled from 'styled-components';
import { Button } from 'reactstrap';
import _ from 'lodash';
import { captureEvent } from '../utils/analytics';
import {
  DEFAULT_BETWEEN_ROW_SPACING,
  DEFAULT_IN_ROW_SPACING,
  NONE,
  UNKNOWN_CROP,
  UNKNOWN_CROP_NAME,
} from '../constants/fields';
import { MapContext } from '../contexts/MapContext';
import { AddCropToFarmMutation, CreateFarmCropMutation } from '../mutations';
import { useQueryWithFarmId } from '../utils/hooks';
import { getFirstQueryResult } from '../utils/graphql';
import GroupedTypeahead from './GroupedTypeahead';
import { sidePanelTypeaheadStyles } from './basics/StandardTypeahead';
import { UncontrolledWhiteTooltip } from './basics/WhiteTooltip';
import { useFarmSettingsContext } from '../contexts/FarmSettingsContext';
import InfoIcon from './icons/InfoIcon';
import { showError } from '../utils/toast';

export const FarmBasicInfoForCropSelection = gql`
  query FarmBasicInfoForCropSelection($farmId: ID!) {
    farm(farmId: $farmId) {
      id
      team {
        id
        name
      }
      growingZone
    }
  }
`;

export const FarmTeamCropTemplatesQuery = gql`
  query FarmTeamCropTemplates($farmId: ID!) {
    farmTeamCropTemplates(farmId: $farmId) {
      id
      name
      description
      team {
        id
      }
    }
  }
`;

export const FarmCropTemplatesQuery = gql`
  query FarmCropTemplates($farmId: ID!) {
    farmCropTemplates(farmId: $farmId) {
      id
      name
      description
      team {
        id
      }
    }
  }
`;

const defaultCrop = {
  betweenRowSpacing: DEFAULT_BETWEEN_ROW_SPACING,
  inRowSpacing: DEFAULT_IN_ROW_SPACING,
  unitCost: 0,
};

const getTemplateOptions = (data, cropMapByTemplate, cropMapByName) => {
  // Only show templates that aren't referenced by any crop in farm
  // And do not have the same name as any crop in farm
  return (getFirstQueryResult(data) || []).filter(
    ({ id, name }) => !cropMapByTemplate[id] && !cropMapByName[name],
  );
};

const customMenuItemChildren = (option) => {
  // Display Team/Public label if in Recent group and has a template associated with it
  const showLabel = option?.header === 'Recently Used' && option?.template;
  return (
    <>
      {showLabel && (
        <small className="ml-1 text-muted">
          {option?.template?.team ? 'Team' : 'Public'}
        </small>
      )}
      {option.id && (
        <UncontrolledWhiteTooltip
          placement="left"
          target={option.id}
          autohide={false}
          flip={false}
          fade={false}
          modifiers={{ preventOverflow: { boundariesElement: 'viewport' } }}
          // Same width as the CropSelector
          $maxWidth="222px"
        >
          <div className="font-small">{option.name}</div>
          {option.description && (
            <div className="font-tiny">{option.description}</div>
          )}
        </UncontrolledWhiteTooltip>
      )}
    </>
  );
};

const GrowingZoneMessage = ({ farmId }) => {
  const { openSettingsModalForFarm } = useFarmSettingsContext();
  return (
    <>
      <div className="mb-3">Type to add crop...</div>
      <div className="text-wrap font-italic">
        <InfoIcon className="mr-1" style={{ marginTop: '-2px' }} />
        Select growing zone in{' '}
        <Button
          color="link"
          size="sm"
          className="p-0 font-italic"
          onClick={() => {
            openSettingsModalForFarm(farmId);
          }}
        >
          project settings
        </Button>{' '}
        to refine crop options.
      </div>
    </>
  );
};

const CropSelector = styled(
  ({
    selectedCrop,
    onCropSelect,
    showNoCropOption,
    className,
    onLoading = _.noop,
    ...props
  }) => {
    const selectorId = useRef(_.uniqueId('crop-selector-'));
    const { farmId } = useParams();
    const [pendingCrop, setPendingCrop] = useState(null);
    const {
      crops: { addCrops, cropNameToId, availableCrops: cropMap },
    } = useContext(MapContext);
    const { data: farmData } = useQueryWithFarmId(
      FarmBasicInfoForCropSelection,
    );
    const { data: defaultData } = useQueryWithFarmId(FarmCropTemplatesQuery, {
      // Get the latest data in case there has been an update
      // to the farm growing zone or the template growing zones.
      fetchPolicy: 'cache-and-network',
    });
    const { data: teamData } = useQueryWithFarmId(FarmTeamCropTemplatesQuery, {
      // Get the latest data in case there has been an update
      // to the farm growing zone or the team template growing zones.
      fetchPolicy: 'cache-and-network',
    });

    const onCropLoaded = useCallback(() => {
      setPendingCrop(null);
      onLoading(false);
    }, [onLoading]);

    const getMutationOptions = (mutationName) => ({
      onCompleted: ({ [mutationName]: { result, errors } }) => {
        if (errors) {
          showError(errors);
        }
        if (result) {
          addCrops([result]);
        } else {
          onCropLoaded();
        }
      },
      onError: (e) => {
        showError(e);
        onCropLoaded();
      },
    });

    const [addCropToFarm] = useMutation(
      AddCropToFarmMutation,
      getMutationOptions('addCrop'),
    );

    const [createCrop] = useMutation(
      CreateFarmCropMutation,
      getMutationOptions('upsertFarmCrop'),
    );

    useEffect(() => {
      if (!pendingCrop) return;
      const addedCrop = cropMap[cropNameToId[pendingCrop.name]];
      if (addedCrop) {
        // Call onCropSelect only after the crop has been added to the map
        onCropSelect(addedCrop);
        onCropLoaded();
      }
    }, [pendingCrop, cropMap, onCropSelect, cropNameToId, onCropLoaded]);

    const onSelect = async ([option]) => {
      if (!option) return;
      if (option.customOption) {
        setPendingCrop(option);
        onLoading(true);
        captureEvent('crop-create', { location: 'design tab' });
        await createCrop({
          variables: {
            input: {
              ...defaultCrop,
              farmId,
              name: option.name,
            },
          },
        });
        // eslint-disable-next-line no-underscore-dangle
      } else if (option?.__typename === 'CropTemplate') {
        setPendingCrop(option);
        onLoading(true);
        await addCropToFarm({ variables: { id: option.id, farmId } });
      } else {
        onCropSelect(option);
        // Clear any crop that was loading
        onCropLoaded();
      }
      captureEvent('crop-selected', option);
    };

    const crop = selectedCrop || UNKNOWN_CROP;

    const cropMapByTemplate = useMemo(
      () =>
        _.keyBy(Object.values(cropMap), (farmCrop) => farmCrop.template?.id),
      [cropMap],
    );

    const recentCropOptions = useMemo(
      () => [
        // Crops already in the farm
        ...Object.values(cropMap),
        ...(showNoCropOption ? [{ name: NONE, __typename: 'Crop' }] : []),
      ],
      [cropMap, showNoCropOption],
    );
    const teamHeader = `${farmData?.farm.team?.name || 'Team'} Templates`;
    const teamCropTemplateOptions = useMemo(
      () => getTemplateOptions(teamData, cropMapByTemplate, cropNameToId),
      [teamData, cropMapByTemplate, cropNameToId],
    );
    const cropTemplateOptions = useMemo(
      () => getTemplateOptions(defaultData, cropMapByTemplate, cropNameToId),
      [defaultData, cropMapByTemplate, cropNameToId],
    );

    const optionGroups = useMemo(() => {
      return [
        // Recently used crops:
        { header: 'Recently Used', options: recentCropOptions },
        // Team crop templates that are not already in the farm:
        { header: teamHeader, options: teamCropTemplateOptions },
        // Crop templates that are not already in the farm:
        {
          header: 'Public Templates',
          options: cropTemplateOptions,
        },
      ];
    }, [
      cropTemplateOptions,
      recentCropOptions,
      teamCropTemplateOptions,
      teamHeader,
    ]);

    const displayName = pendingCrop
      ? 'Loading...'
      : !crop.name || crop.name === UNKNOWN_CROP_NAME
      ? 'Select Crop'
      : crop.name;

    const noGrowingZone =
      farmData?.farm &&
      farmData?.farm?.id &&
      farmData?.farm?.growingZone == null;

    return (
      <div className={className}>
        <GroupedTypeahead
          id={selectorId.current}
          align="right"
          allowNew
          clearTextOnFocus
          customMenuItemChildren={customMenuItemChildren}
          disabled={pendingCrop !== null}
          key={crop?.name}
          labelKey="name"
          newSelectionPrefix="Add crop: "
          newSelectionPrompt={
            // If no growing zone is set for the farm, show the growing zone message:
            noGrowingZone ? (
              <GrowingZoneMessage farmId={farmData.farm.id} />
            ) : (
              // Otherwise, show the default prompt:
              'Type to add crop...'
            )
          }
          onChange={onSelect}
          optionGroups={optionGroups}
          placeholder={displayName}
          selectedOption={selectedCrop}
          showTitle={false}
          size="sm"
          {...props}
        />
      </div>
    );
  },
)`
  ${sidePanelTypeaheadStyles}
`;

export default CropSelector;
